import cx from 'classnames'
import './styles.scss'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'

import { Chevron } from '../../claim-history/components/select/chevron'
import { List, ListHeader, ListItem } from '../NarusMaterial'

const RenderHeader = ({ label, url, basepath, active, onShowMore }) => {
  return (
    <ListHeader
      url={url && `${basepath}${url}`}
      active={active !== undefined && url === active}
      className='sidebar-list-header'
      onClick={onShowMore}
    >
      {label}
      {onShowMore && <Chevron />}
    </ListHeader>
  )
}

const findActiveTab = (struct, active) => {
  return struct.find((list) => {
    const [ header, ...listItems ] = list
    const [ , url ] = header

    if (url === active) {
      return true
    }

    return listItems.some((item) => {
      const [ , url ] = item
      return url === active
    })
  })
}

const RenderTopLevelLists = ({ struct, basepath, active }) => {
  let activeTab = useMemo(() => findActiveTab(struct, active), [ active, struct ])

  const [ selected, setSelected ] = useState([])

  useEffect(() => {
    if (!activeTab?.[0]?.[0]) {
      return
    }

    if (!selected.length > 0) {
      setSelected([ activeTab[0][0] ])
    }
  }, [ activeTab, selected.length ])

  return struct.map((list, key) => {
    const [ header, ...listItems ] = list
    const [ headerLabel, headerUrl ] = header

    let handleSelect = (label) => {
      if (!selected.includes(label)) {
        setSelected((prev) => [ ...prev, label ])
      }

      if (activeTab?.[0]?.[0] === label) {
        return
      }

      let selectedIndex = selected.findIndex((item) => item === label)

      if (selectedIndex === -1) {
        return
      }

      setSelected((prev) => ([
        ...prev.slice(0, selectedIndex),
        ...prev.slice(selectedIndex + 1, prev.length)
      ]))
    }

    return (
      <List
        key={key}
        className='sidebar-list'
      >
        {header &&
          <RenderHeader
            label={headerLabel}
            url={headerUrl}
            basepath={basepath}
            active={active}
            onShowMore={listItems.length ? () => handleSelect(headerLabel) : undefined}
          />
        }
        <div className={cx('sidebar-list-items-wrapper', selected.includes(headerLabel) ? 'shown' : 'collapsed')}>
          {listItems?.map((item, key) => {
            const [ text, url ] = item
            return (
              <ListItem
                className='sidebar-nav-item'
                key={key}
                url={url && `${basepath}${url}`}
                active={active !== undefined && url === active}
                data-test={
                  active !== undefined && url === active && 'highlighted-list-item'
                }
              >
                {text}
              </ListItem>
            )
          })}
        </div>
      </List>
    )
  })
}

const RenderToggleButton = ({ onClick, collapsed }) => {
  return (
    <button
      className={cx("sidebar-collapse-toggle", { collapsed })}
      onClick={onClick}
      type="button"
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <line x1="9" x2="9" y1="3" y2="21" />
      </svg>
    </button>
  )
}

const RenderToggle = ({ noToggle, toggle, visible }) => {
  if (noToggle) {
    return null
  }
  return (
    <div
      className={cx('sidebar-toggle', {
        'no-hide': !visible
      })}
      onClick={toggle}
      aria-hidden='true'
    >
      <i className='fa fa-chevron-left' />
    </div>
  )
}

function SideNav ({ active, structure, basepath, toggle, noToggle, visible }) {
  // 1. Every item of this array will be separate list
  // 2. First item of list should be array also. Array can have 1 or 2 items. It will be a ListHeader
  // 2.1 First item of ListHeader array - text, second is the url, where header leads to
  // 3. Rest items of list are ListItems. List item array can have 1 or 2 items length and behaves like ListHeader.
  //
  //  Example Structure of Navigation
  // [
  //   [["Care Plan", "/igao"]],
  //   [
  //     ["Demographics"],
  //     ["Patient", "/standarddemographicspatient"],
  //     ["Insurance", "/standarddemographicsinsurance"],
  //     ["Home Assessment", "/standarddemographicshomeassessment"],
  //     ["Family | Support", "/standarddemographicssupport"],
  //     ["Provider Network", "/standarddemographicsmedical"],
  //     ["Narus Team", "/standarddemographicsnarusteam"]
  //   ],
  // ];

  // New toggle button replaces old functionality
  const handleNewToggle = () => {
    if (toggle) {
      toggle() // Call parent toggle to hide/show sidebar (affects layout)
    }
  }

  return (
    <div className='left-nav'>
      {/* Toggle button - in header when expanded, fixed when collapsed */}
      {!noToggle && (
        <div className={cx("sidebar-toggle-container", { collapsed: !visible })}>
          <RenderToggleButton onClick={handleNewToggle} collapsed={!visible} />
        </div>
      )}

      {/* Sidebar content */}
      {visible && (
        <Navigation
          struct={structure}
          basepath={basepath}
          active={active}
        />
      )}
    </div>
  )
}

function Navigation ({ ...rest }) {
  return (
    <div className='wrapper'>
      <RenderTopLevelLists{...rest} />
    </div>
  )
}

SideNav.propTypes = {
  active: PropTypes.string,
  toggle: PropTypes.func,
  structure: PropTypes.instanceOf(Array).isRequired,
  basepath: PropTypes.string,
  noToggle: PropTypes.bool
}

export default SideNav
